import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
    KLUCZ_ZGODY_POWROT, KLUCZ_ZGODY_W_TOKU, PREFIKS_STANU_ZGODY, WAZNOSC_POWROTU_MS, czytajSesje, powrotSwiezy, zapiszSesje,
} from "./RegulyIncydentow/wspolne";

export const ADRES_INTEGRACJI = "/admin/integrations";
export const SEKCJA_REGUL = "reguly-incydentow";
/** Jak długo wynik powrotu czeka na zalogowanie w mPracowniku i na ekran (jedna stała dla całej sekcji). */
export { WAZNOSC_POWROTU_MS };
/** Ile razy komponent ponawia przejście do Integracji, gdy inna nawigacja zabrała adres w tej samej chwili (strona główna →
 *  pulpit, ekran logowania → pulpit). Granica chroni przed dokładaniem się do pętli przekierowań, gdyby konkurencyjna nawigacja
 *  wygrywała bez końca. Odesłanie z Integracji PO dotarciu (np. wymuszona zmiana hasła) kończy pracę komponentu od razu. */
export const MAKS_PROB_PRZEJSCIA = 4;

/** Czy bieżący adres to Integracje (ukośnik na końcu nie ma znaczenia). */
export const naAdresieIntegracji = (sciezka) => String(sciezka || "").replace(/\/+$/, "") === ADRES_INTEGRACJI;

/**
 * Rozpoznaje powrót ze strony zgody administratora Microsoft w adresie bieżącej strony. Zwraca:
 *  – `null`, gdy w adresie nie ma `state` z przedrostkiem `o365zgody.` — wtedy NIE robi nic (logowanie Microsoft używa fragmentu
 *    adresu, nie parametrów; cudzych parametrów nie ruszamy);
 *  – `{ zgodny: false }`, gdy `state` nie zgadza się z zapisanym w pamięci tej karty (obcy albo powtórzony powrót) — parametry są
 *    czyszczone z adresu, nic więcej się nie dzieje;
 *  – `{ zgodny: true, state, companyId, rejestracja, error, errorDescription, adresPowrotu, tenantNazwa }` — powrót tej karty.
 * `state` jest JEDNORAZOWY dla rozpoznania powrotu: po rozpoznaniu znika z pamięci „zgoda w toku”. Idzie dalej do bloku zgód, który
 * podaje go serwerowi przy sprawdzeniu po powrocie — serwer sprawdza wtedy świeżo mimo limitu minuty (tylko dla `state`, który sam
 * wydał). Wynik zgody NIE jest brany z parametru `admin_consent` — ekran ustala go ponownym sprawdzeniem tokenem.
 */
export const przechwycPowrotZeZgody = (okno = window) => {
    let parametry;
    try {
        parametry = new URLSearchParams(okno.location.search || "");
    } catch {
        return null;
    }
    const state = parametry.get("state") || "";
    if (!state.startsWith(PREFIKS_STANU_ZGODY)) return null;

    const error = parametry.get("error");
    const errorDescription = parametry.get("error_description");
    // Parametry powrotu znikają z adresu od razu (identyfikator tenanta i treść błędu nie zostają w historii przeglądarki).
    try {
        ["state", "admin_consent", "tenant", "error", "error_description", "error_uri", "scope"].forEach((p) => parametry.delete(p));
        const reszta = parametry.toString();
        okno.history.replaceState(okno.history.state, "", okno.location.pathname + (reszta ? `?${reszta}` : "") + (okno.location.hash || ""));
    } catch {
        /* adres zostaje, jak był — obsługa powrotu działa dalej */
    }

    const wToku = czytajSesje(KLUCZ_ZGODY_W_TOKU);
    zapiszSesje(KLUCZ_ZGODY_W_TOKU, null);
    if (!wToku || wToku.state !== state || wToku.companyId == null) return { zgodny: false };
    return {
        zgodny: true,
        state,
        companyId: wToku.companyId,
        rejestracja: wToku.rejestracja || "czytelnik",
        adresPowrotu: wToku.adresPowrotu || null,
        tenantNazwa: wToku.tenantNazwa || null,
        error: error || null,
        errorDescription: errorDescription || null,
    };
};

/**
 * [O365 etapy 3–7, 2026-10-05] POWRÓT ZE STRONY ZGODY ADMINISTRATORA MICROSOFT (zamontowany raz w App.js, niczego nie rysuje).
 *
 * Microsoft odsyła przeglądarkę na adres główny aplikacji z parametrami `state`, `admin_consent`, `tenant` (albo `error`,
 * `error_description`). Komponent rozpoznaje WYŁĄCZNIE własny `state` (przedrostek `o365zgody.` + zgodność z pamięcią karty),
 * czyści parametry z adresu, zapisuje w pamięci karty „wynik czeka” i — gdy użytkownik jest zalogowany w mPracowniku — przechodzi
 * do Integracje → Reguły incydentów → karta spółki. Tam blok zgód sam woła sprawdzenie z wymuszeniem świeżego tokenu.
 *
 * Powrót bez sesji w mPracowniku (wygasła w czasie logowania w Microsoft): wynik zostaje w pamięci karty i jest obsłużony zaraz po
 * zalogowaniu.
 *
 * [domknięcie po krytyku 2026-10-06] PRZEJŚCIE JEST PONAWIANE, DOPÓKI ADRES NIE JEST ADRESEM INTEGRACJI. Było źle: komponent wołał
 * `navigate` raz i od razu zapisywał „przekierowano”. W tym samym zatwierdzeniu Reacta nawigowała też strona główna (`HomeRedirect`
 * → pulpit), a po logowaniu ekran logowania (→ pulpit). Efekt komponentu stojącego wcześniej w drzewie wykonuje się pierwszy, więc
 * wygrywała tamta nawigacja — administrator po nadaniu zgody lądował na pulpicie bez słowa, a zapisane „przekierowano” blokowało
 * drugą próbę. Teraz komponent patrzy na adres: „przekierowano” zapisuje dopiero PO DOTARCIU, a dopóki adres jest inny, ponawia
 * przejście (najwyżej MAKS_PROB_PRZEJSCIA razy — potem odpuszcza, wynik zostaje dla bloku zgód na kwadrans). Zapadka na prawdziwym
 * routerze: pages/admin/office365/__tests__/powrotZeZgodyRouter.dom.o365.test.js.
 */
export default function PowrotZeZgodyO365() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { user, loading } = useAuth() || {};
    const proby = useRef(0);
    // Przechwycenie w PIERWSZYM renderze — zanim przekierowanie strony głównej zdąży zgubić parametry adresu.
    const [czeka, setCzeka] = useState(() => {
        const powrot = przechwycPowrotZeZgody();
        if (powrot?.zgodny) {
            zapiszSesje(KLUCZ_ZGODY_POWROT, { ...powrot, kiedy: Date.now() });
            return true;
        }
        // Wynik zapamiętany przed zalogowaniem (poprzednie załadowanie strony) — ważny kwadrans, przekierowuje tylko raz.
        const zapamietany = czytajSesje(KLUCZ_ZGODY_POWROT);
        if (!zapamietany) return false;
        if (zapamietany.przekierowano || !powrotSwiezy(zapamietany)) {
            if (!zapamietany.przekierowano) zapiszSesje(KLUCZ_ZGODY_POWROT, null);
            return false;
        }
        return true;
    });

    useEffect(() => {
        if (!czeka || loading || !user) return;
        const powrot = czytajSesje(KLUCZ_ZGODY_POWROT);
        // Blok zgód zdążył już odebrać wynik (albo pamięć karty jest niedostępna) — nie ma dokąd prowadzić.
        if (!powrot || powrot.companyId == null) { setCzeka(false); return; }
        const dotarl = naAdresieIntegracji(pathname);
        if (dotarl || proby.current >= MAKS_PROB_PRZEJSCIA) {
            // „Przekierowano” dopiero TERAZ: po dotarciu (albo po wyczerpaniu prób) odświeżenie strony nie ściąga już na Integracje.
            zapiszSesje(KLUCZ_ZGODY_POWROT, { ...powrot, przekierowano: true });
            setCzeka(false);
            return;
        }
        proby.current += 1;
        navigate(ADRES_INTEGRACJI, { replace: true, state: { section: SEKCJA_REGUL, companyId: powrot.companyId } });
    }, [czeka, loading, user, navigate, pathname]);

    return null;
}
